import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { ILuckyDrawDisplaySetting, IMerchant, IMission, IUploadReceiptSetting } from "@interfaces";

import { merchantApi } from "@api";
import { enumTheme } from "@configs";
import { store } from "@redux";
import { useNotify } from "@utils";
import { RootState } from ".";

const initialState: Partial<IMerchant> = {
    name: "",
    _id: "",
    themes: {
        // ...store.getState().themes,
        themeMode: enumTheme.LIGHT,
        themeColor: "",
    },
    enableAccess: true,
};

export const getMerchant = createAsyncThunk(
    "merchant/getMerchant",
    async (values, { rejectWithValue }) => {
        try {
            const res = await merchantApi.getMerchantNew();
            return res.data.data as IMerchant;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);
export const setMerchantSetting = createAsyncThunk(
    "merchant/setMerchantSetting",
    async (values, { rejectWithValue }) => {
        const state = store.getState() as RootState;
        try {
            const memberShipTiers = await merchantApi.getMemberShipTiers(state.merchant?._id || "");
            const advanceSettings = await merchantApi.getAdvanceSettings(state.merchant?._id || "");
            const signUpSettings = await merchantApi.getSignUpSettings(state.merchant?._id || "");
            return {
                ...state.merchant,
                memberShipTiers: memberShipTiers.data.data,
                advanceSettings: advanceSettings.data.data,
                signUpSettings: signUpSettings.data.data,
            } as IMerchant;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);
export const getUploadReceiptSetting = createAsyncThunk(
    "merchant/uploadReceiptSetting",
    async (values, { rejectWithValue }) => {
        const state = store.getState() as RootState;
        try {
            const res = await merchantApi.getUploadReceiptSetting(state.merchant?._id || "");
            return res.data.data as IUploadReceiptSetting;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);
export const getLuckyDrawSettings = createAsyncThunk(
    "merchant/getLuckyDrawSettings",
    async (values, { rejectWithValue }) => {
        const state = store.getState() as RootState;
        try {
            const res = await merchantApi.getLuckyDrawSettings(state.merchant?._id || "");
            return res.data.data as ILuckyDrawDisplaySetting;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);
export const getMission = createAsyncThunk(
    "merchant/getMission",
    async (values, { rejectWithValue }) => {
        const state = store.getState() as RootState;
        try {
            const res = await merchantApi.getMissionSetting(state.merchant?._id || "");
            if (res.data.data.reason) {
                useNotify().error(res.data.data.reason);
            }
            return res.data.data as IMission;
        } catch (err: any) {
            return rejectWithValue(err);
        }
    }
);

const merchant = createSlice({
    name: "theme",
    initialState,
    reducers: {
        setMainColor: (state, action: PayloadAction<string>) => {
            if (state.themes) state.themes.themeColor = action.payload;
        },
        setMerchant: (state, action: PayloadAction<IMerchant>) => {
            state = { ...state, ...action.payload };
            state.themes = state.themes || { themeMode: enumTheme.LIGHT };
            state.themes.themeMode = state.themes?.themeMode || enumTheme.LIGHT;
            return state as IMerchant;
        },
        resetMerchant: () => {
            localStorage.clear();
            sessionStorage.clear();
            return initialState;
        },
    },
    extraReducers(builder) {
        builder.addCase(getMerchant.fulfilled, (state, action: { payload: IMerchant }) => {
            return {
                ...state,
                ...action.payload,
                missionDisplaySetting: state.missionDisplaySetting,
            };
        });
        builder.addCase(getMerchant.rejected, (state) => {
            state = initialState;
        });
        builder.addCase(setMerchantSetting.fulfilled, (state, action: { payload: IMerchant }) => {
            return {
                ...state,
                ...action.payload,
                missionDisplaySetting: state.missionDisplaySetting,
            };
        });
        builder.addCase(setMerchantSetting.rejected, (state) => {
            state = initialState;
        });
        builder.addCase(
            getUploadReceiptSetting.fulfilled,
            (state, action: { payload: IUploadReceiptSetting }) => {
                state.uploadReceiptSetting = action.payload;
            }
        );

        builder.addCase(getUploadReceiptSetting.rejected, (state) => {
            state.uploadReceiptSetting = undefined;
        });
        builder.addCase(
            getLuckyDrawSettings.fulfilled,
            (state, action: { payload: ILuckyDrawDisplaySetting }) => {
                state.luckyDrawDisplaySetting = action.payload;
            }
        );

        builder.addCase(getLuckyDrawSettings.rejected, (state) => {
            state.luckyDrawDisplaySetting = undefined;
        });
        builder.addCase(getMission.fulfilled, (state, action: { payload: IMission }) => {
            state.missionDisplaySetting = action.payload;
        });
        builder.addCase(getMission.rejected, (state) => {
            state.missionDisplaySetting = undefined;
        });
    },
});

export const selectMerchant = (state: RootState) => state.merchant;

export const { setMainColor, setMerchant, resetMerchant } = merchant.actions;

export default merchant.reducer;
